import {
  Outlet,
  useLocation,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/solid-router";
import { createEffect, createMemo, onCleanup, onMount, Show } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { appState, useInteractionFlag } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { SkipToContent } from "@/components/skip-to-content";
import { AppErrorBoundary } from "@/components/app-error-boundary";

// Layout module parts
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { getGlowColors } from "../helpers";
import type { AppLayoutSearch } from "../app-search";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

/**
 * Generic `_app` layout shell: themed background + sidebar + header +
 * offline banner + animated <Outlet/> + shortcuts modal.
 *
 * SCAFFOLD (F0a): no domain surfaces. F0b+ hangs its views,
 * drawers and URL-addressable dialogs here.
 */
export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // The initial-sync overlay waits for the shell to mount.
    appState.markAsReady();
    // props.db is used when the domain (F0b+) mounts surfaces dependent
    // on the local DB; the shell itself does not need it.
    void props.db;
  });

  const navigate = useNavigate();

  const rawLocation = useLocation();
  const routerState = useRouterState();

  const location = createMemo(() => rawLocation());

  // Section key for the route entrance animation (slide-up + fade).
  const routeSectionKey = createMemo(() => location().pathname || "/");

  const isNavigating = () => routerState().status === "pending";

  const theme = useTheme();

  const { isSidebarOpen, setIsSidebarOpen, shortcutsOpen, setShortcutsOpen } =
    // `location` is passed by reference (the accessor), not read here;
    // `useAppDialogs` tracks it internally.
    // oxlint-disable-next-line solid/reactivity
    useAppDialogs(location);

  // ── URL-addressability of global overlays (#15) ───────────────────────────
  // The URL is the source of truth for which overlay is open.
  const layoutSearch = useSearch({ from: "/_app" });

  const setLayoutSearch = (patch: Partial<AppLayoutSearch>) => {
    void navigate({
      to: ".",
      search: (prev) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined) {
            delete next[k];
          } else {
            next[k] = v;
          }
        }
        return next;
      },
      replace: true,
    });
  };

  const setShortcutsModalOpen = (open: boolean) => {
    setLayoutSearch({ modal: open ? "shortcuts" : undefined });
  };

  // ── URL → signals (init + back/forward + pasted link) ──
  createEffect(() => {
    const s = layoutSearch();
    setShortcutsOpen(s.modal === "shortcuts");
  });

  useAppShortcuts({
    navigate,
    theme,
    setShortcutsOpen: setShortcutsModalOpen,
    setIsSidebarOpen,
  });

  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). Ownership is announced in the interaction context rather than
  // re-derived from the DOM by each gesture.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  const globalTheme = createMemo(
    () => theme.allThemes()?.find((r) => r.target_id === "global") ?? null,
  );

  const glows = createMemo(() => getGlowColors(globalTheme(), theme.tokens()));

  return (
    <>
      {/* A11y: keyboard-only skip link → jumps focus past the shell to
          `#main-content` on the first Tab. */}
      <SkipToContent />
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={theme.isBlur()}
        background={theme.tokens()?.background}
        patternType={theme.tokens()?.patternType}
        patternOpacity={theme.tokens()?.patternOpacity}
        {...glows()}
      >
        <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

        {/* ── Main content ───────────────────────────────────────── */}
        <main
          id="main-content"
          class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]"
        >
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
            <div class="flex flex-1 flex-col min-h-0 h-full">
              {/* Inline guard: a render error in the active route keeps the
                  shell (sidebar/header) intact and offers Retry/Reload. */}
              <AppErrorBoundary inline label="route">
                {/* Per-route entrance: keying on the section path remounts this
                    wrapper on navigation, replaying `slide-in-up` (GPU-only:
                    transform + opacity). `prefers-reduced-motion` is honored
                    globally in global.css. */}
                <Show when={routeSectionKey()} keyed>
                  <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                    <Outlet />
                  </div>
                </Show>
              </AppErrorBoundary>
            </div>
          </div>
        </main>

        <AppLayoutDialogs shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsModalOpen} />
      </AppBackground>
    </>
  );
}
